import { APIError } from "../errors";
import { makeQueryString } from "../utils";
import config from "config";
import camelCase from "lodash/camelCase";
import words from "lodash/words";

const BASE_PATH = "api";
const SCHEME = config.get("apiScheme") as string;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type CasingIgnorePaths = string[];

export type ApiSettings = {
  domain: string;
  hasMultipleQueryParamValues: boolean;
  headers: Record<string, string | undefined>;
};

const snakeCase = (text: string): string =>
  words(text).reduce(
    (result, word, index) =>
      result + (index > 0 ? "_" : "") + word.toLowerCase(),
    "",
  );

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === "object" &&
  Object.getPrototypeOf(value) === Object.prototype;

function deepTransformKeys<T>(
  obj: T,
  keyFn: (key: string) => string,
  ignorePaths: CasingIgnorePaths = [],
): T {
  if (ignorePaths.length === 1 && ignorePaths[0] === "*") return obj;
  if (Array.isArray(obj)) {
    return obj.map((item) => deepTransformKeys(item, keyFn, ignorePaths)) as T;
  }
  if (isPlainObject(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        const newKey = UUID_RE.test(key) ? key : keyFn(key);
        if (ignorePaths.includes(newKey) || ignorePaths.includes(key)) {
          return [newKey, value];
        }
        return [newKey, deepTransformKeys(value, keyFn, ignorePaths)];
      }),
    ) as T;
  }
  return obj;
}

export const deepCamelCaseKeys = <T>(
  obj: T,
  ignorePaths?: CasingIgnorePaths,
): T => deepTransformKeys(obj, camelCase, ignorePaths);

const toSnakeCase = <T>(obj: T, ignorePaths?: CasingIgnorePaths): T =>
  deepTransformKeys(obj, snakeCase, ignorePaths);

export const makeUrl = (
  resource: string,
  params: Record<string, unknown> | null | undefined,
  settings: ApiSettings,
): string => {
  const path = resource.endsWith("/") ? resource : `${resource}/`;
  const url = `${SCHEME}${settings.domain}/${BASE_PATH}/${path}`;
  return `${url}${makeQueryString(params, settings.hasMultipleQueryParamValues)}`;
};

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return cookie ? cookie.slice(prefix.length) : null;
}

type CallOptions = {
  method: string;
  data?: unknown;
};

async function call<T>(
  resource: string,
  params: Record<string, unknown> | null | undefined,
  settings: ApiSettings,
  callOptions: CallOptions,
  ignorePaths?: CasingIgnorePaths,
): Promise<T> {
  const filteredHeaders = Object.fromEntries(
    Object.entries({
      "Content-Type": callOptions.data != null ? "application/json" : undefined,
      "X-Requested-With": "XMLHttpRequest",
      "x-csrftoken": getCookie("csrftoken") ?? undefined,
      ...settings.headers,
    }).filter(([, value]) => value != null),
  ) as Record<string, string>;

  const url = makeUrl(resource, params, settings);

  const response = await fetch(url, {
    body:
      callOptions.data != null ? JSON.stringify(callOptions.data) : undefined,
    credentials: "include",
    headers: filteredHeaders,
    method: callOptions.method.toUpperCase(),
  });

  if (!response.ok) {
    const rawBody = await response.json().catch(() => ({}));
    const body = rawBody ? deepCamelCaseKeys(rawBody, ignorePaths) : {};
    const responseInfo = {
      body,
      headers: Object.fromEntries(Array.from(response.headers.entries())),
      status: response.status,
      url,
    };
    const requestInfo = { body: params, headers: filteredHeaders };
    throw new APIError(response.statusText, responseInfo, requestInfo);
  }

  const rawData =
    response.status !== 204 ? await response.json().catch(() => ({})) : {};
  return (rawData ? deepCamelCaseKeys(rawData, ignorePaths) : {}) as T;
}

export const get = <T = unknown>(
  resource: string,
  params: Record<string, unknown> | undefined,
  settings: ApiSettings,
  ignorePaths?: CasingIgnorePaths,
): Promise<T> =>
  call<T>(resource, params, settings, { method: "get" }, ignorePaths);

export const post = <T = unknown>(
  resource: string,
  data: unknown,
  settings: ApiSettings,
  ignorePaths?: CasingIgnorePaths,
): Promise<T> =>
  call<T>(
    resource,
    null,
    settings,
    { data: toSnakeCase(data, ignorePaths), method: "post" },
    ignorePaths,
  );

export const patch = <T = unknown>(
  resource: string,
  data: unknown,
  settings: ApiSettings,
  ignorePaths?: CasingIgnorePaths,
): Promise<T> =>
  call<T>(
    resource,
    null,
    settings,
    { data: toSnakeCase(data, ignorePaths), method: "patch" },
    ignorePaths,
  );

export const put = <T = unknown>(
  resource: string,
  data: unknown,
  settings: ApiSettings,
  ignorePaths?: CasingIgnorePaths,
): Promise<T> =>
  call<T>(
    resource,
    null,
    settings,
    { data: toSnakeCase(data, ignorePaths), method: "put" },
    ignorePaths,
  );

export const del = <T = unknown>(
  resource: string,
  data: unknown,
  settings: ApiSettings,
  ignorePaths?: CasingIgnorePaths,
): Promise<T> =>
  call<T>(
    resource,
    null,
    settings,
    { data: toSnakeCase(data, ignorePaths), method: "delete" },
    ignorePaths,
  );
